import React from 'react'
import { connect, mapProps } from '@formily/react'
import type { Field as FormField } from '@formily/core'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { HelpCircle } from 'lucide-react'
import { isVoidField } from '@formily/core'

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="form-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function FormMessage({
  children,
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="form-message"
      className={cn('text-red-500 text-sm', className)}
      {...props}
    >
      {children}
    </p>
  )
}

interface FormItemProps {
  children?: React.ReactNode
  label?: string
  required?: boolean
  field?: FormField
  className?: string
  feedbackText?: string
  extra?: React.ReactNode
  asterisk?: boolean
  tooltip?: React.ReactNode
  labelWidth?: number | string
  labelAlign?: 'left' | 'right'
  wrapperAlign?: 'left' | 'right'
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
  readOnly?: boolean
  feedbackStatus?: 'error' | 'warning' | 'success' | 'pending'
  feedbackIcon?: React.ReactNode
  feedbackLayout?: 'loose' | 'terse' | 'popover' | 'none'
  fullness?: boolean
  inset?: boolean
  gridSpan?: number
  isInlineField?: boolean
}

const BaseFormItem = ({
  children,
  label,
  required,
  className,
  feedbackText,
  extra,
  tooltip,
  labelWidth,
  labelAlign = 'left',
  wrapperAlign = 'left',
  size = 'medium',
  disabled,
  readOnly,
  feedbackStatus,
  feedbackIcon,
  feedbackLayout = 'loose',
  fullness,
  inset,
  gridSpan,
  asterisk,
  isInlineField,
  ...props
}: FormItemProps) => {
  const error = feedbackStatus === 'error'

  if (isInlineField && label) {
    return (
      <div
        data-slot="form-item"
        className={cn(
          'flex items-center justify-between gap-4 mb-3',
          className,
        )}
      >
        <div className="flex items-center gap-2">
          <Label
            data-slot="form-label"
            data-error={!!error}
            className={cn(
              'data-[error=true]:text-red-500 leading-6 text-sm',
              className,
            )}
            style={{ width: labelWidth }}
          >
            {label}
            {asterisk && <span className="text-red-500">*</span>}
          </Label>
          {tooltip && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          )}
        </div>

        <div data-slot="form-control" className="flex-shrink-0">
          {children}
        </div>

        {feedbackText && feedbackLayout !== 'none' && (
          <FormMessage>{feedbackText}</FormMessage>
        )}

        {extra && <FormDescription>{extra}</FormDescription>}
      </div>
    )
  }

  return (
    <div data-slot="form-item" className={cn('grid gap-2 mb-4', className)}>
      {label && (
        <div className={cn('flex items-center gap-2')}>
          <Label
            data-slot="form-label"
            data-error={!!error}
            className={cn(
              'data-[error=true]:text-red-500 leading-6',
              className,
            )}
            style={{ width: labelWidth }}
          >
            {label}
            {asterisk && <span className="text-red-500">*</span>}
          </Label>
          {tooltip && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      <div data-slot="form-control">{children}</div>

      {feedbackText && feedbackLayout !== 'none' && (
        <FormMessage>{feedbackText}</FormMessage>
      )}

      {extra && <FormDescription>{extra}</FormDescription>}
    </div>
  )
}

export const FormilyFormItem = connect(
  BaseFormItem,
  mapProps((props, field) => {
    if (isVoidField(field))
      return {
        label: field.title || props.label,
        asterisk: props.asterisk,
        extra: props.extra || field.description,
      }
    if (!field) return props
    const takeFeedbackStatus = () => {
      if (field.validating) return 'pending'
      return field.decoratorProps.feedbackStatus || field.validateStatus
    }
    const takeMessage = () => {
      const split = (messages: any[]) => {
        return messages.reduce((buf, text, index) => {
          if (!text) return buf
          return index < messages.length - 1
            ? buf.concat([text, ', '])
            : buf.concat([text])
        }, [])
      }
      if (field.validating) return
      if (props.feedbackText) return props.feedbackText
      if (field.selfErrors.length) return split(field.selfErrors)
      if (field.selfWarnings.length) return split(field.selfWarnings)
      if (field.selfSuccesses.length) return split(field.selfSuccesses)
    }
    const takeAsterisk = () => {
      if (field.required && field.pattern !== 'readPretty') {
        return true
      }
      if ('asterisk' in props) {
        return props.asterisk
      }
      return false
    }

    // Check if this is the instructions field and modify the label
    let label = props.label || field.title
    if (
      field.path?.toString() === 'instructions' ||
      (typeof label === 'string' &&
        label.toLowerCase().includes('any more instructions'))
    ) {
      label = 'Custom Instructions (optional)'
    }

    // Check if this field should use inline layout based on component type
    // Debug log to see field properties
    if (typeof window !== 'undefined') {
      console.log('Field info:', {
        componentType: field.componentType,
        component: field.component,
        displayName: field.componentType,
        path: field.path?.toString(),
        title: field.title,
      })
    }

    // Check component type for inline layout
    const isInlineField =
      field.componentType === 'Switch' || field.componentType === 'Slider'

    return {
      label: label,
      feedbackStatus: takeFeedbackStatus(),
      feedbackText: takeMessage(),
      asterisk: takeAsterisk(),
      optionalMarkHidden:
        field.pattern === 'readPretty' && !('asterisk' in props),
      extra: props.extra || field.description,
      isInlineField: isInlineField, // Pass this to BaseFormItem
    }
  }),
)
